import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { getUserProfile } from '../api/users';
import { Avatar } from '../components/Avatar';
import { LoadingAnimation } from '../components/LoadingAnimation';

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get current user ID from Keycloak to check if viewing own profile
  const currentUserId = keycloak?.tokenParsed?.sub || '';
  const isOwnProfile = currentUserId === userId;

  // Redirect to own profile page if user is viewing their own profile
  useEffect(() => {
    if (isOwnProfile && userId) {
      console.log('🔄 User viewing own profile, redirecting to /profile...');
      navigate('/profile', { replace: true });
      return;
    }
  }, [isOwnProfile, userId, navigate]);

  useEffect(() => {
    console.log('UserProfile mounted');
    console.log('userId:', userId);
    console.log('keycloak authenticated:', keycloak?.authenticated);
    console.log('keycloak token exists:', !!keycloak?.token);
    
    // Check if userId is 'login' which is a routing error
    if (userId === 'login') {
      console.log('🔄 Invalid userId "login" detected, redirecting to profile page...');
      navigate('/profile', { replace: true });
      return;
    }
    
    if (keycloak?.token && userId) {
      loadUserProfile();
    } else {
      console.log('Missing requirements - token:', !!keycloak?.token, 'userId:', userId);
      setIsLoading(false);
      if (!userId) {
        setError('User ID not provided in URL');
      } else if (!keycloak?.token) {
        setError('Authentication required');
      }
    }
  }, [userId, keycloak?.token, navigate]);

  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Loading profile for userId:', userId);
      console.log('Current user ID from token:', keycloak?.tokenParsed?.sub);
      
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      if (!keycloak?.token) {
        throw new Error('Authentication token not available');
      }
      
      const profileData = await getUserProfile(keycloak.token, userId);
      console.log('Profile data received:', profileData);
      setProfile(profileData);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      
      // Handle specific HTTP errors
      if (error instanceof Error) {
        if (error.message.includes('422')) {
          setError('User profile not found or invalid. This may be a new user account that needs profile setup.');
        } else if (error.message.includes('404')) {
          setError('User profile not found. Please contact support if this issue persists.');
        } else if (error.message.includes('403')) {
          setError('You do not have permission to view this profile.');
        } else if (error.message.includes('401')) {
          setError('Authentication expired. Please log in again.');
        } else {
          setError(`Failed to load profile: ${error.message}`);
        }
      } else {
        setError('Failed to load user profile');
      }
      
      // Don't show toast for 422 errors as they're common for new users
      if (error instanceof Error && !error.message?.includes('422')) {
        toast.error('Failed to load user profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentView = async (documentUrl?: string) => {
    if (!documentUrl || !keycloak?.token) {
      toast.error('Document not available');
      return;
    }

    try {
      console.log('Downloading document with token:', documentUrl);
      
      const response = await fetch(documentUrl, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + keycloak.token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const openableTypes = ['pdf', 'image', 'text', 'video', 'audio'];
      const contentType = response.headers.get('content-type') || '';
      const canOpen = openableTypes.some(type => 
        contentType.toLowerCase().includes(type)
      );

      if (canOpen) {
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to view document:', error);
      toast.error('Failed to view document');
    }
  };

  // Debug render
  console.log('UserProfile render - isLoading:', isLoading, 'error:', error, 'profile:', !!profile);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingAnimation />
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Profile</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-x-2">
              <button 
                onClick={loadUserProfile}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry
              </button>
              {error?.includes('422') && (
                <Link
                  to="/profile"
                  className="inline-block px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Create Profile
                </Link>
              )}
              <Link
                to="/projects"
                className="inline-block px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Back to Projects
              </Link>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            <p>Debug info:</p>
            <p>User ID: {userId || 'Not provided'}</p>
            <p>Authenticated: {keycloak?.authenticated ? 'Yes' : 'No'}</p>
            <p>Token: {keycloak?.token ? 'Available' : 'Missing'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Profile not found</p>
          <Link
            to="/projects"
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Avatar
              firstName={profile.first_name}
              lastName={profile.last_name}
              email={profile.email}
              size={80}
              src={profile.photo_url}
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {profile.first_name} {profile.last_name}
              </h1>
              <p className="text-gray-600">{profile.email}</p>
              <p className="text-gray-500">{profile.city || 'Not specified'}, {profile.region || 'Not specified'}</p>
              {profile.rate > 0 && (
                <p className="text-green-600 font-semibold">${profile.rate}/hour</p>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            {isOwnProfile && (
              <Link
                to="/profile"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Edit My Profile
              </Link>
            )}
            <Link
              to="/projects"
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Back to Projects
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-500">Name:</span>
              <p className="text-gray-900">{profile.first_name} {profile.last_name}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Email:</span>
              <p className="text-gray-900">{profile.email}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Phone:</span>
              <p className="text-gray-900">{profile.phone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        {/* Location & Professional Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Location & Professional Details</h2>
          
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-500">Location:</span>
              <p className="text-gray-900">{profile.city || 'Not specified'}, {profile.region || 'Not specified'}</p>
            </div>
            {profile.rate > 0 && (
              <div>
                <span className="text-sm font-medium text-gray-500">Hourly Rate:</span>
                <p className="text-gray-900">${profile.rate}/hour</p>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-gray-500">Work Range:</span>
              <p className="text-gray-900">{profile.range_miles} miles</p>
            </div>
          </div>
        </div>

        {/* Skills */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Skills & Expertise</h2>
          
          <div className="flex flex-wrap gap-2">
            {profile.skills && profile.skills.length > 0 ? (
              profile.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {skill}
                </span>
              ))
            ) : (
              <p className="text-gray-500">No skills listed</p>
            )}
          </div>
        </div>

        {/* Projects */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Projects</h2>
          
          {profile.projects && profile.projects.length > 0 ? (
            <div className="space-y-2">
              {profile.projects.map((project, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-md">
                  <p className="font-medium">{project.name || `Project ${index + 1}`}</p>
                  {project.description && (
                    <p className="text-sm text-gray-500">{project.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No projects assigned</p>
          )}
        </div>

        {/* Documents (only show if user has uploaded documents) */}
        {(profile.nda_document || profile.contract_document || profile.driver_license_document) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4">Documents</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {profile.nda_document && (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                  <div>
                    <p className="font-medium">NDA Document</p>
                    <p className="text-sm text-gray-500">Available</p>
                  </div>
                  <button
                    onClick={() => handleDocumentView(profile.nda_document)}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                </div>
              )}

              {profile.contract_document && (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                  <div>
                    <p className="font-medium">Contract Document</p>
                    <p className="text-sm text-gray-500">Available</p>
                  </div>
                  <button
                    onClick={() => handleDocumentView(profile.contract_document)}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                </div>
              )}

              {profile.driver_license_document && (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                  <div>
                    <p className="font-medium">Driver's License</p>
                    <p className="text-sm text-gray-500">Available</p>
                  </div>
                  <button
                    onClick={() => handleDocumentView(profile.driver_license_document)}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile; 